import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useState } from 'react';
import { Analytics } from '@/utils/analytics';
import { Buffer } from 'buffer';
import { Button } from './button';
import { captureException } from '@/utils/sentry';
import { CombinedActionSheet } from './combined-action-sheet';
import { formatCurrency } from '@/utils/money';
import { ListItemSeparator } from './list-item';
import { PrinterSelectList } from './printer-select-list';
import { RENDER_TAG_PRINT_BATCH_QUERY } from './tag-print-batch-queries';
import { renderZPLTemplate } from '@/utils/tag-printing';
import { StyleSheet, Text, View } from 'react-native';
import {
  TagPrint_RenderBatchQuery,
  TagPrint_RenderBatchQueryVariables,
} from '@/types';
import { TagPrintingBluetoothPermissions } from './tag-printing-bluetooth-permissions';
import { TYPOGRAPHY } from '@/styles';
import { useAdaptVariantPackToItemListOption } from '@/contexts/item-context-utils';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useBluetoothContext } from '@/contexts/bluetooth-context';
import { useLazyQuery } from '@/hooks/use-lazy-query';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useOfflineTagBatchPrint } from '@/hooks/use-offline-data';
import { useToasts } from '@/contexts/toast-context';

enum TagPrintBatchConfirmationRoute {
  BluetoothPermissions = 'BLUETOOTH_PERMISSIONS',
  PrinterSelection = 'PRINTER_SELECTION',
}

interface Props {
  onClose: () => void;
  onComplete: () => void;
  printTagBatchUUID: string;
  selectedOfflineTemplateUUID: string | null;
  tagCount: number;
}

export const TagPrintBatchConfirmationActionSheet: React.FC<Props> = ({
  onClose,
  onComplete,
  printTagBatchUUID,
  selectedOfflineTemplateUUID,
  tagCount,
}) => {
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();
  const { hasPermission, selectedDevice } = useBluetoothContext();
  const isOfflineModeActive = useOfflineModeActive();
  const adaptVariantPackToItemListOption =
    useAdaptVariantPackToItemListOption();
  const { showErrorToast } = useToasts();

  const [isDeviceConnected, setIsDeviceConnected] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  useEffect(() => {
    const checkConnectedDevice = async () => {
      if (selectedDevice) {
        const isConnected = await selectedDevice.isConnected();
        if (!isConnected) {
          showErrorToast(i18n.t('bluetooth_device.disconnected'));
        }

        setIsDeviceConnected(isConnected);
      }
    };

    checkConnectedDevice();
  }, [selectedDevice, showErrorToast]);

  const renderBatchTemplates = useLazyQuery<
    TagPrint_RenderBatchQuery,
    TagPrint_RenderBatchQueryVariables
  >(RENDER_TAG_PRINT_BATCH_QUERY, {
    fetchPolicy: 'no-cache',
  });

  const readyToPrint = selectedDevice && isDeviceConnected;

  const { tagBatchPrintData: offlineTagBatchPrintData } =
    useOfflineTagBatchPrint(restaurantUUID, supplierUUID);

  const selectedOfflineBatch =
    offlineTagBatchPrintData?.batches?.[printTagBatchUUID];

  const selectedOfflineBatchTemplateUUID =
    selectedOfflineBatch?.print_tag_template.uuid;

  const selectedOfflineTemplate = selectedOfflineTemplateUUID
    ? offlineTagBatchPrintData?.templates?.[selectedOfflineTemplateUUID]
    : selectedOfflineBatchTemplateUUID
      ? offlineTagBatchPrintData?.templates?.[selectedOfflineBatchTemplateUUID]
      : null;

  const selectedOfflinePrinterTemplate =
    selectedOfflineTemplate?.offline_printer_text_template;

  const getOfflineRenderTemplate = useCallback(() => {
    if (!Boolean(selectedOfflinePrinterTemplate)) {
      return null;
    }
    if (!selectedOfflineBatch) {
      return null;
    }
    return selectedOfflineBatch.print_tag_batch_variant_packs.map(
      (variantPack) => {
        const vp = variantPack.variant_pack;
        const { metadata } = adaptVariantPackToItemListOption(vp);
        const formattedPrice = vp.restaurant_variant_pack_overrides[0]
          ?.user_defined_metadata?.list_prices?.retail_price_in_micros
          ? formatCurrency(
              vp.restaurant_variant_pack_overrides[0].user_defined_metadata
                .list_prices.retail_price_in_micros,
            )
          : undefined;

        return renderZPLTemplate(selectedOfflinePrinterTemplate!, {
          displayName: metadata?.displayName ?? '',
          externalItemId: metadata?.externalID ?? '',
          formattedPrice,
          packDisplayString: metadata?.packDisplayString ?? '',
        });
      },
    );
  }, [
    adaptVariantPackToItemListOption,
    selectedOfflineBatch,
    selectedOfflinePrinterTemplate,
  ]);

  const getOnlineRenderTemplate = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const { data } = await renderBatchTemplates({
        variables: {
          printTagBatchUUID,
          restaurantUUID,
          supplierUUID,
        },
      });

      const uniqueTagCount = new Set(
        data.renderPrintTagBatchTemplatesForRestaurant.map(
          (response) => response.variant_pack_id,
        ),
      ).size;

      Analytics.track('batch_tag_print.submit_button.press', {
        deviceName: selectedDevice!.name, // because this function is guarded by readyToPrint condition
        printTagBatchUUID,
        tagCount,
        uniqueTagCount,
      });

      return data?.renderPrintTagBatchTemplatesForRestaurant.map(
        (response) => response.rendered_template,
      );
    } catch (e) {
      captureException(e);
      showErrorToast();
    }

    setLoadingTemplates(false);
  }, [
    printTagBatchUUID,
    renderBatchTemplates,
    restaurantUUID,
    selectedDevice,
    showErrorToast,
    supplierUUID,
    tagCount,
  ]);

  const onSubmit = useCallback(async () => {
    if (readyToPrint) {
      const renderedTemplates = isOfflineModeActive
        ? getOfflineRenderTemplate()
        : await getOnlineRenderTemplate();
      if (!renderedTemplates) {
        showErrorToast();
        return;
      }

      try {
        const combinedBuffer = Buffer.from(renderedTemplates.join('')).toString(
          'base64',
        );
        await selectedDevice.write(combinedBuffer, 'base64');
        onComplete();
      } catch (e) {
        captureException(e);
        showErrorToast();
      }
    }
  }, [
    readyToPrint,
    isOfflineModeActive,
    getOfflineRenderTemplate,
    getOnlineRenderTemplate,
    showErrorToast,
    selectedDevice,
    onComplete,
  ]);

  return (
    <CombinedActionSheet
      initialRoute={
        hasPermission
          ? TagPrintBatchConfirmationRoute.PrinterSelection
          : TagPrintBatchConfirmationRoute.BluetoothPermissions
      }
      onClose={onClose}
      renderScene={({ jumpTo, routeKey }) => {
        switch (routeKey) {
          case TagPrintBatchConfirmationRoute.BluetoothPermissions:
            return (
              <TagPrintingBluetoothPermissions
                goBack={onClose}
                onPermissionsGranted={() =>
                  jumpTo(TagPrintBatchConfirmationRoute.PrinterSelection)
                }
              />
            );
          case TagPrintBatchConfirmationRoute.PrinterSelection:
            return (
              <>
                <View style={styles.header}>
                  <Text style={TYPOGRAPHY.navigationTitle}>
                    {i18n.t('select_printer')}
                  </Text>
                  <Text style={[TYPOGRAPHY.p, styles.messageText]}>
                    {i18n.t('select_printer.message')}
                  </Text>
                </View>
                <PrinterSelectList
                  containerStyle={styles.printerSelect}
                  ItemSeparatorComponent={() => (
                    <ListItemSeparator style={styles.printerListSeparator} />
                  )}
                  listItemStyle={styles.printerSelectListItem}
                />
                <Button
                  disabled={!readyToPrint}
                  loading={loadingTemplates}
                  onPress={onSubmit}
                  // Tracking event is tracked in onSubmit
                  trackingData={null}
                >
                  {tagCount > 1
                    ? i18n.t('print_tag_plural', { count: tagCount })
                    : i18n.t('print_tag')}
                </Button>
              </>
            );
        }
      }}
      routes={{
        [TagPrintBatchConfirmationRoute.BluetoothPermissions]: {
          previousKey: null,
          showCloseButton: false,
        },
        [TagPrintBatchConfirmationRoute.PrinterSelection]: {
          previousKey: null,
          showCloseButton: false,
        },
      }}
    />
  );
};

const styles = StyleSheet.create({
  header: {
    gap: 4,
  },
  messageText: {
    lineHeight: 22,
  },
  printerListSeparator: {
    marginHorizontal: 0,
  },
  printerSelect: {
    marginVertical: 16,
  },
  printerSelectListItem: {
    paddingHorizontal: 0,
  },
});
